// @ts-nocheck
import { MODULE_ACL, USER_ROLES } from "./constant";
import { isAclModuleOptionsRelevant } from "./aclModuleOptionsAccess";
import { parseModuleOperations } from "./voicePlanAcl";

export const CALL_GROUP_ACL_MODULE = MODULE_ACL.CALL_GROUP;

export const CALL_GROUP_EDIT_OPTION_DEFS = [
  {
    value: "member_management",
    label: "Member management",
    description: "Add / edit / remove members (agent assignment & priority)",
  },
  {
    value: "call_strategy",
    label: "Call strategy",
    description: "Assign / change call strategy (routing algorithm)",
  },
  {
    value: "group_time_schedule",
    label: "Group time schedule",
    description: "Configure call group business hours / time schedule",
  },
  {
    value: "agent_whisper",
    defaultOn: false,
    label: "Agent whisper",
    description: "Configure agent whisper (pre-connect prompt)",
  },
  {
    value: "inbound_disposition",
    label: "Inbound disposition form",
    description: "Attach / edit / remove inbound disposition form",
  },
  {
    value: "outbound_disposition",
    label: "Outbound disposition form",
    description: "Attach / edit / remove outbound disposition form",
  },
  {
    value: "call_script",
    label: "Call script",
    description: "Attach / edit / remove call script",
  },
  {
    value: "multi_ivr_sticky",
    defaultOn: false,
    label: "Multi IVR sticky",
    description: "Change multi IVR sticky (on / off)",
  },
  {
    value: "waiting_audio",
    defaultOn: false,
    label: "Waiting audio",
    description: "Change waiting audio (music on hold / ringing)",
  },
  {
    value: "sequential_call_forwarding",
    defaultOn: false,
    label: "Sequential call forwarding",
    description: "Change sticky member (off / hard sticky / soft sticky)",
  },
  {
    value: "no_agent_prompt",
    label: "No agent prompt",
    description: "Select or change the no-agent-available prompt",
  },
  {
    value: "inbound_numbers",
    label: "Inbound numbers",
    description: "Assign inbound numbers and dedicated CLI for this call group",
  },
];

const ALL_OPTION_VALUES = CALL_GROUP_EDIT_OPTION_DEFS.map((o) => o.value);

export const DEFAULT_CALL_GROUP_EDIT_OPTIONS = CALL_GROUP_EDIT_OPTION_DEFS.filter(
  (o) => o.defaultOn !== false
).map((o) => o.value);

function isSuperAdminRole(userRole, isSuperAdmin) {
  return isSuperAdmin || userRole === USER_ROLES.SUPERADMIN;
}

export function getCallGroupAclEntry(moduleOperations) {
  const ops = parseModuleOperations(moduleOperations);
  return ops[CALL_GROUP_ACL_MODULE] || null;
}

export function normalizeCallGroupEditOptions(options) {
  if (!Array.isArray(options)) return [];
  return [
    ...new Set(
      options
        .map((o) => String(o).trim())
        .filter((o) => ALL_OPTION_VALUES.includes(o))
    ),
  ];
}

export function getAllowedCallGroupEditOptions(
  moduleOperations,
  { userRole = null, isSuperAdmin = false } = {}
) {
  if (isSuperAdminRole(userRole, isSuperAdmin)) {
    return ALL_OPTION_VALUES;
  }

  const entry = getCallGroupAclEntry(moduleOperations);
  if (!entry?.edit) {
    return [];
  }

  const options = normalizeCallGroupEditOptions(entry.edit_options);
  if (options.length === 0) {
    return null;
  }

  return options;
}

export function canEditCallGroupOption(
  moduleOperations,
  optionKey,
  { userRole = null, isSuperAdmin = false } = {}
) {
  if (isSuperAdminRole(userRole, isSuperAdmin)) {
    return true;
  }

  const allowed = getAllowedCallGroupEditOptions(moduleOperations, {
    userRole,
    isSuperAdmin,
  });
  if (allowed === null) {
    return true;
  }
  return allowed.includes(optionKey);
}

export function isCallGroupEditAclConfigured(moduleEntry) {
  if (!moduleEntry?.edit) return false;
  return normalizeCallGroupEditOptions(moduleEntry.edit_options).length > 0;
}

export function getCallGroupEditOptionsForEditor(
  moduleOperations,
  isSuperAdmin
) {
  const allowed = getAllowedCallGroupEditOptions(moduleOperations, {
    isSuperAdmin,
  });
  if (isSuperAdmin || allowed === null) {
    return CALL_GROUP_EDIT_OPTION_DEFS;
  }
  return CALL_GROUP_EDIT_OPTION_DEFS.filter((o) => allowed.includes(o.value));
}

export function getParentGrantableCallGroupEditOptions(
  moduleOperations,
  isSuperAdmin
) {
  if (isSuperAdmin) return ALL_OPTION_VALUES;
  const allowed = getAllowedCallGroupEditOptions(moduleOperations, {
    isSuperAdmin: false,
  });
  if (allowed === null) {
    return ALL_OPTION_VALUES;
  }
  return allowed;
}

export function normalizeCallGroupEditOptionsForSave(
  options,
  parentGrantable = null
) {
  const pool =
    parentGrantable && parentGrantable.length > 0
      ? parentGrantable
      : ALL_OPTION_VALUES;
  return normalizeCallGroupEditOptions(options).filter((o) => pool.includes(o));
}

export function formatCallGroupEditOptionsLabel(moduleEntry) {
  if (!isAclModuleOptionsRelevant(moduleEntry)) return "—";
  const options = normalizeCallGroupEditOptions(moduleEntry.edit_options);
  if (options.length === 0) return "—";
  if (options.length === ALL_OPTION_VALUES.length) {
    return "All options";
  }
  return `${options.length} option(s)`;
}

/** Resolve module_operations from a logged-in user record (storage / profile). */
export function getUserModuleOperations(user) {
  if (!user) return {};
  const raw =
    user?.acl_settings?.module_operations ?? user?.module_operations ?? null;
  return parseModuleOperations(raw);
}
